import { useId, useState } from 'react'
import { normalizeInspectionHints } from '../lib/inspectionHints'

const number = value => Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value).toLocaleString('ru-RU') : '—'

const year = value => Number.isFinite(Number(value)) && Number(value) > 0 ? String(Number(value)) : '—'
const share = (part, whole) => Number(whole) > 0 && Number(part) >= 0 ? Math.min(100, Math.round(Number(part) / Number(whole) * 100)) : null

const hintStyles = `
.ih{background:white;border-radius:18px;box-shadow:0 1px 2px rgba(16,24,40,.04),0 4px 14px -6px rgba(16,24,40,.08);overflow:hidden}
.ih-toggle{position:relative;width:100%;display:flex;align-items:center;justify-content:space-between;gap:10px;padding:12px 14px;border:0;background:linear-gradient(120deg,#F4F7FF 0%,#F8F4FF 100%);text-align:left;font:inherit;cursor:pointer}
.ih-toggle-main{display:flex;align-items:center;gap:10px;min-width:0}
.ih-icon{display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:32px;height:32px;border-radius:10px;background:linear-gradient(135deg,#3B78FF,#7C3AED);color:white;box-shadow:0 6px 14px -8px rgba(91,60,237,.9)}
.ih-icon i{font-size:16px}
.ih-name{display:block;font-size:12px;font-weight:700;color:#1A1A1A}
.ih-car{display:block;font-size:10px;color:#6B7280;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ih-chevron{display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:28px;height:28px;border-radius:9px;background:rgba(255,255,255,.8);color:#9CA3AF;transition:transform .2s}
.ih-chevron[data-open=true]{transform:rotate(180deg)}
.ih-chevron i{font-size:15px}
.ih-tabs{display:flex;gap:2px;margin:12px 14px 0;padding:3px;border-radius:10px;background:#F3F5F9}
.ih-tabs button{flex:1;min-width:0;display:flex;align-items:center;justify-content:center;gap:5px;padding:7px 6px;border:0;border-radius:8px;background:transparent;color:#6B7280;font:inherit;font-size:11px;line-height:1.4;font-weight:600;cursor:pointer}
.ih-tabs button i{font-size:13px}
.ih-tabs button[aria-selected=true]{background:white;color:#0052FF;box-shadow:0 1px 3px rgba(16,24,40,.12)}
.ih-list{display:flex;flex-direction:column;gap:8px;padding:0 14px 14px}
.ih-entry{display:flex;gap:10px;padding:10px 12px;border-radius:12px;background:#F7F8FA}
.ih-entry-icon{display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:24px;height:24px;border-radius:8px;background:#EEF3FF;color:#0052FF}
.ih-entry-icon[data-tone=history]{background:#FFF7E6;color:#D97706}
.ih-entry-icon i{font-size:13px}
.ih-entry-body{min-width:0;flex:1}
.ih-entry-body p{margin:0}
.ih-entry-title{font-size:12px;font-weight:700;color:#1E40AF;margin-bottom:3px!important}
.ih-entry-text{font-size:11px;color:#374151;line-height:1.5}
.ih-share{display:flex;align-items:center;gap:8px;margin-top:8px}
.ih-share-bar{flex:1;height:5px;border-radius:3px;background:#E6EAF1;overflow:hidden}
.ih-share-bar>span{display:block;height:100%;border-radius:3px;background:linear-gradient(90deg,#FBBF24,#F59E0B)}
.ih-share b{font-size:11px;font-weight:700;color:#B45309}
.ih-evidence{margin-top:6px!important;font-size:10px;color:#6B7280;line-height:1.5}
.ih-context{display:flex;flex-direction:column;gap:6px;padding:0 14px 10px;font-size:10px;color:#6B7280;line-height:1.5}
.ih-chips{display:flex;flex-wrap:wrap;gap:6px}
.ih-chips span{display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:999px;background:#F3F5F9;color:#374151;font-weight:600}
.ih-chips i{font-size:12px;color:#9CA3AF}
.ih-context p{margin:0}
.ih-warning{display:flex;align-items:center;gap:5px;color:#B45309}
.ih-context summary{cursor:pointer;color:#0052FF;font-weight:600}
.ih-empty{margin:0;padding:0 14px 14px;font-size:11px;color:#6B7280;line-height:1.5}
.ih button:focus-visible,.ih [role=tabpanel]:focus-visible,.ih summary:focus-visible{outline:2px solid #0052FF;outline-offset:-2px}
`

export default function InspectionHints({ hints, brand, model, initiallyCollapsed = true }) {
    const [collapsed, setCollapsed] = useState(initiallyCollapsed)
    const [tab, setTab] = useState('attention')
    const id = useId()
    const data = normalizeInspectionHints(hints)
    if (!data || (data.legacy && !data.attention.length)) return null
    const context = data.context
    const intro = (title, description) => <header data-hints-intro="true" style={{ padding: '12px 14px 10px' }}>
        <h3 style={{ margin: '0 0 3px', fontSize: '12px', fontWeight: '700', color: '#1A1A1A', lineHeight: '1.5' }}>{title}</h3>
        <p style={{ margin: 0, fontSize: '10px', color: '#6B7280', lineHeight: '1.5' }}>{description}</p>
    </header>
    const entry = (hint, index, history = false) => {
        const percent = history ? share(hint.evidence.finding_visits, hint.evidence.checked_visits) : null
        return <div key={history ? hint.id : index} className="ih-entry">
            <span className="ih-entry-icon" data-tone={history ? 'history' : undefined}><i className={`ti ${history ? 'ti-chart-bar' : 'ti-bulb'}`} aria-hidden="true" /></span>
            <div className="ih-entry-body">
                <p className="ih-entry-title">{hint.title}</p>
                <p className="ih-entry-text">{hint.text}</p>
                {history && percent !== null && <div className="ih-share" aria-hidden="true">
                    <span className="ih-share-bar"><span style={{ width: `${Math.max(percent, 3)}%` }} /></span>
                    <b>{percent}%</b>
                </div>}
                {history && <p className="ih-evidence">
                    Замечания: {number(hint.evidence.finding_visits)} из {number(hint.evidence.checked_visits)} диагностик
                    {Number(hint.evidence.checked_vehicles) > 0
                        ? ` · ${number(hint.evidence.finding_vehicles)} из ${number(hint.evidence.checked_vehicles)} идентифицированных автомобилей`
                        : ' · Число автомобилей не определено'}
                    {Number(hint.evidence.unknown_vehicle_visits) > 0 && <><br />Без надёжного идентификатора: {number(hint.evidence.unknown_vehicle_visits)} диагностик.</>}
                </p>}
            </div>
        </div>
    }
    return <section aria-label="Подсказки перед осмотром" className="ih">
        <style>{hintStyles}</style>
        <button type="button" className="ih-toggle" onClick={() => setCollapsed(value => !value)} aria-expanded={!collapsed}>
            <span className="ih-toggle-main">
                <span className="ih-icon"><i className="ti ti-sparkles" aria-hidden="true" /></span>
                <span style={{ minWidth: 0 }}><span className="ih-name">Подсказки ИИ</span>
                    <span className="ih-car">{brand} {model}</span></span>
            </span>
            <span className="ih-chevron" data-open={!collapsed}><i aria-hidden="true" className="ti ti-chevron-down" /></span>
        </button>
        {!collapsed && <div>
            {!data.legacy && <div role="tablist" aria-label="Источники подсказок" className="ih-tabs">
                {['attention', 'history'].map((key, index) => <button key={key} type="button" role="tab"
                    id={`${id}-${key}-tab`} aria-controls={`${id}-${key}-panel`} aria-selected={tab === key} tabIndex={tab === key ? 0 : -1}
                    onClick={() => setTab(key)} onKeyDown={event => {
                        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
                        event.preventDefault()
                        const next = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : 1 - index
                        setTab(next === 0 ? 'attention' : 'history')
                        event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next].focus()
                    }}>
                    <i className={`ti ${key === 'attention' ? 'ti-bulb' : 'ti-chart-bar'}`} aria-hidden="true" />
                    {key === 'attention' ? 'Слабые места · ИИ' : 'Статистика компании'}
                </button>)}
            </div>}
            <div id={`${id}-attention-panel`} role={data.legacy ? undefined : 'tabpanel'} aria-labelledby={data.legacy ? undefined : `${id}-attention-tab`} hidden={!data.legacy && tab !== 'attention'} tabIndex={0}>
                {intro('На что обратить внимание', 'По знаниям ИИ · Модель, год и пробег. Поводы для проверки, не диагноз.')}
                {data.attention.length ? <div className="ih-list">{data.attention.map((hint, index) => entry(hint, index))}</div>
                    : <p className="ih-empty">Достаточно уверенные модельные подсказки не выделены. Это не означает отсутствие неисправностей; выполните стандартную диагностику.</p>}
            </div>
            {!data.legacy && <div id={`${id}-history-panel`} role="tabpanel" aria-labelledby={`${id}-history-tab`} hidden={tab !== 'history'} tabIndex={0}>
                {intro('Находки в похожих автомобилях', 'По данным сети · Реальные наблюдения на похожих автомобилях, не диагноз текущей машины.')}
                <div className="ih-context">
                    <div className="ih-chips">
                        <span><i className="ti ti-calendar" aria-hidden="true" />{year(context.yearFrom)}–{year(context.yearTo)} г.</span>
                        <span><i className="ti ti-gauge" aria-hidden="true" />{number(context.mileageFrom)}–{number(context.mileageTo)} км</span>
                    </div>
                    <p>Диагностик: {number(context.visits)} · Идентифицированных автомобилей: {number(context.identifiedVehicles)}.</p>
                    {Number(context.visits) > 0 && Number(context.identifiedVehicles) < 5 && <p className="ih-warning"><i className="ti ti-alert-circle" aria-hidden="true" />Малая выборка — вывод о типичности делать нельзя.</p>}
                    <details>
                        <summary>Как считается статистика</summary>
                        <p style={{ margin: '5px 0 0' }}>Все повторные визиты учтены. Число автомобилей — оценка по VIN и госномеру, не статистика повторных поломок. Доля относится только к выбранным диагностикам с проверкой пункта, не ко всем автомобилям модели.</p>
                    </details>
                </div>
                {data.history.length ? <div className="ih-list">{data.history.map((hint, index) => entry(hint, index, true))}</div>
                    : <p className="ih-empty">
                        {!Number(context.visits) ? 'Завершённых диагностик в выбранном диапазоне пока нет.'
                            : !Number(context.candidateCount) ? 'В подходящих диагностиках замечаний по проверенным пунктам не зафиксировано.'
                                : 'Из подходящих наблюдений отдельные рекомендации не выбраны.'}
                    </p>}
            </div>}
        </div>}
    </section>
}
